/**
 * Cosmetics panel mounted inside the profile screen.
 *
 * Layout (inspired by the user's reference screenshot but cat-flavoured):
 *
 *   ┌────────────────┬────────────────────────────────────────┐
 *   │ UNIT           │           [ kingdom picker ]            │
 *   │ ─────          │                                         │
 *   │ Bagarreur ▶    │           ◖  body + weapons  ◗          │
 *   │ Pêcheur        │                                         │
 *   │ Furieux        ├─────────────────────────────────────────┤
 *   │ Tireur précis  │  WEAPON │ BADGE                          │
 *   │ Aristochat     │  ─────                                   │
 *   │ Légende        │  [card] [card] [card] [card] [card]      │
 *   │                │  [card] [card] …                         │
 *   └────────────────┴─────────────────────────────────────────┘
 *
 * Clicking a weapon card LIVE-previews it next to the selected kingdom's
 * head. Locked weapons show their price; clicking them unlocks (deducts
 * shinies) before equipping. Paladin uniquely has a slot toggle (right /
 * left) inside the preview so the user can pick two weapons.
 */

import { auth } from '../auth/state.js';
import {
  fetchWeaponCatalog,
  unlockWeapon,
  saveLoadout,
  type WeaponCatalog,
  type WeaponDef,
  type UnitType,
  type WeaponCategory,
  type CosmeticLoadout,
} from '../auth/api.js';
import { fetchFactions, type FactionDef } from '../lobby/api.js';
import { el } from './login.js';
import { invalidateArmyTextureCache } from './in_game.js';

interface CosmeticsCtx {
  catalog: WeaponCatalog;
  factions: FactionDef[];
  previewFactionId: string;
  selectedUnit: UnitType;
  /** Paladin only: which hand we are currently editing. */
  paladinSlot: 'right' | 'left';
  /** Currently focused category tab. Defaults to a sensible per-unit tab. */
  selectedCategory: WeaponCategory;
}

let ctxState: CosmeticsCtx | null = null;
let rootEl: HTMLElement | null = null;

/** Best default category to focus when the user clicks a unit type. */
function defaultCategoryFor(unit: UnitType, slot: 'right' | 'left'): WeaponCategory {
  if (unit === 'archer' || unit === 'crossbowman') return 'bow';
  if (unit === 'paladin' && slot === 'left') return 'shield';
  if (unit === 'berserker') return 'axe';
  if (unit === 'hero') return 'sword';
  return 'sword';
}

/** Read the currently-equipped weapon id for the active slot, or '' if none. */
function currentWeaponId(ctx: CosmeticsCtx): string {
  const lo = auth.user?.cosmeticLoadout ?? {};
  if (ctx.selectedUnit === 'paladin') {
    const p = lo.paladin ?? {};
    return ctx.paladinSlot === 'right'
      ? p.right ?? ctx.catalog.defaultLoadout.paladin.right
      : p.left ?? ctx.catalog.defaultLoadout.paladin.left;
  }
  const fromLo = (lo as Record<string, string | undefined>)[ctx.selectedUnit];
  return fromLo ?? (ctx.catalog.defaultLoadout[ctx.selectedUnit] as string);
}

/** Does a weapon fit the current unit-slot? */
function isCompatible(w: WeaponDef, ctx: CosmeticsCtx): boolean {
  if (ctx.selectedUnit === 'paladin' && ctx.paladinSlot === 'left') {
    return !!w.paladinLeft;
  }
  return w.unitTypes.includes(ctx.selectedUnit);
}

/** True if the user owns (default or unlocked) this weapon. */
function isOwned(weaponId: string): boolean {
  const w = ctxState?.catalog.weapons.find((x) => x.id === weaponId);
  if (!w) return false;
  if (w.price === 0) return true;
  return (auth.user?.cosmeticUnlocks ?? []).includes(weaponId);
}

/** Build a NEW CosmeticLoadout object reflecting an equip action. */
function buildLoadoutWith(
  current: CosmeticLoadout,
  unit: UnitType,
  slot: 'right' | 'left',
  weaponId: string,
): CosmeticLoadout {
  const next: CosmeticLoadout = { ...current };
  if (unit === 'paladin') {
    next.paladin = { ...(current.paladin ?? {}) };
    if (slot === 'right') next.paladin.right = weaponId;
    else next.paladin.left = weaponId;
  } else {
    (next as Record<string, unknown>)[unit] = weaponId;
  }
  return next;
}

export async function renderCosmetics(container: HTMLElement): Promise<void> {
  rootEl = container;
  if (!auth.user) {
    container.innerHTML = '';
    container.appendChild(el('p', 'auth-error', 'Connexion requise.'));
    return;
  }
  // Re-mount path: the router re-dispatches whenever `auth.user` changes
  // (which we do on every equip / unlock to keep the navbar in sync).
  // Reusing the existing ctxState here means the user's current selection
  // (unit, paladin slot, category, preview kingdom) survives the redraw
  // instead of snapping back to "Bagarreur".
  if (ctxState) {
    redrawAll();
    return;
  }
  // First mount: hydrate the catalog + faction list, then init state.
  container.innerHTML = '';
  const loading = el('div', 'cosm-loading', '🐾 Chargement de la chatterie…');
  container.appendChild(loading);
  try {
    const [catalog, factions] = await Promise.all([
      fetchWeaponCatalog(),
      fetchFactions(),
    ]);
    ctxState = {
      catalog,
      factions,
      previewFactionId: factions[0]?.id ?? 'saliana',
      selectedUnit: 'warrior',
      paladinSlot: 'right',
      selectedCategory: defaultCategoryFor('warrior', 'right'),
    };
    // Safe-remove: a concurrent redrawAll() (triggered by auth.subscribe
    // → router.dispatch) may have already wiped the host innerHTML.
    if (loading.isConnected) loading.remove();
    redrawAll();
  } catch (err) {
    console.error(err);
    if (loading.isConnected) loading.remove();
    container.appendChild(el('p', 'auth-error', 'Impossible de charger les cosmétiques.'));
  }
}

function redrawAll(): void {
  if (!rootEl || !ctxState) return;
  rootEl.innerHTML = '';
  const wrap = el('div', 'cosm-wrap');
  wrap.appendChild(buildHeader(ctxState));
  wrap.appendChild(buildBody(ctxState));
  rootEl.appendChild(wrap);
}

/** Title + shinies counter row. */
function buildHeader(ctx: CosmeticsCtx): HTMLElement {
  const head = el('div', 'cosm-header');
  const title = document.createElement('h2');
  title.className = 'cosm-title';
  const titleIcon = document.createElement('img');
  titleIcon.src = '/sprites/iconchat_crier.png';
  titleIcon.className = 'cosm-title-icon';
  titleIcon.alt = '';
  title.appendChild(titleIcon);
  title.appendChild(document.createTextNode('Arsenal du minou'));
  head.appendChild(title);
  const shinies = el('div', 'cosm-shinies');
  const star = document.createElement('img');
  star.className = 'cosm-shinies-icon';
  star.src = '/sprites/iconchat_valide.png';
  star.alt = '';
  shinies.appendChild(star);
  const count = document.createElement('span');
  count.className = 'cosm-shinies-count';
  count.textContent = `${auth.user?.shinies ?? 0}`;
  shinies.appendChild(count);
  const label = document.createElement('span');
  label.className = 'cosm-shinies-label';
  label.textContent = 'Moustaches d\'or';
  shinies.appendChild(label);
  head.appendChild(shinies);
  // Hint about how rewards work
  const hint = el(
    'p',
    'cosm-hint',
    'Gagne des Moustaches d\'or en finissant 1ᵉʳ, 2ᵉ ou 3ᵉ — plus il y a de patrouilleurs, plus le butin est gros.',
  );
  head.appendChild(hint);
  // Kingdom preview picker (a strip of mini-heads)
  const kingRow = el('div', 'cosm-king-row');
  kingRow.appendChild(el('span', 'cosm-king-label', 'Royaume aperçu :'));
  const heads = el('div', 'cosm-king-heads');
  for (const f of ctx.factions) {
    const head = document.createElement('button');
    head.type = 'button';
    head.className = 'cosm-king-head' + (f.id === ctx.previewFactionId ? ' is-active' : '');
    head.title = f.name;
    head.style.setProperty('--faction-color', f.color);
    const img = document.createElement('img');
    img.src = `/sprites/${f.id}_army.png`;
    img.alt = f.name;
    head.appendChild(img);
    head.addEventListener('click', () => {
      ctx.previewFactionId = f.id;
      redrawAll();
    });
    heads.appendChild(head);
  }
  kingRow.appendChild(heads);
  head.appendChild(kingRow);
  return head;
}

/** Two-column body: left rail (unit selector) + right pane (preview + grid). */
function buildBody(ctx: CosmeticsCtx): HTMLElement {
  const body = el('div', 'cosm-body');
  body.appendChild(buildUnitRail(ctx));
  body.appendChild(buildRightPane(ctx));
  return body;
}

/** Left rail: list of unit types. */
function buildUnitRail(ctx: CosmeticsCtx): HTMLElement {
  const rail = el('aside', 'cosm-rail');
  rail.appendChild(el('div', 'cosm-rail-title', 'UNITÉ'));
  const units: UnitType[] = ['warrior', 'archer', 'berserker', 'crossbowman', 'paladin', 'hero'];
  for (const u of units) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cosm-rail-item' + (u === ctx.selectedUnit ? ' is-active' : '');
    btn.textContent = ctx.catalog.unitLabels[u];
    btn.addEventListener('click', () => {
      ctx.selectedUnit = u;
      if (u !== 'paladin') ctx.paladinSlot = 'right';
      ctx.selectedCategory = defaultCategoryFor(u, ctx.paladinSlot);
      redrawAll();
    });
    rail.appendChild(btn);
  }
  return rail;
}

/** Right pane: preview window on top, weapon grid below. */
function buildRightPane(ctx: CosmeticsCtx): HTMLElement {
  const pane = el('section', 'cosm-pane');
  pane.appendChild(buildPreview(ctx));
  pane.appendChild(buildCategoryTabs(ctx));
  pane.appendChild(buildGrid(ctx));
  return pane;
}

/** Live preview window: faction head + currently-equipped weapons. */
function buildPreview(ctx: CosmeticsCtx): HTMLElement {
  const card = el('div', 'cosm-preview');
  const faction = ctx.factions.find((f) => f.id === ctx.previewFactionId);
  card.style.setProperty('--faction-color', faction?.color ?? '#94774a');
  // Stack: [left weapon?] [body] [right weapon]
  const stack = el('div', 'cosm-preview-stack');
  const lo = auth.user?.cosmeticLoadout ?? {};
  const isPaladin = ctx.selectedUnit === 'paladin';
  // Resolve the right / left ids for THIS unit (with defaults fallback).
  let rightId: string;
  let leftId: string | null = null;
  if (isPaladin) {
    const p = lo.paladin ?? {};
    rightId = p.right ?? ctx.catalog.defaultLoadout.paladin.right;
    leftId = p.left ?? ctx.catalog.defaultLoadout.paladin.left;
  } else {
    rightId = (lo as Record<string, string | undefined>)[ctx.selectedUnit]
      ?? (ctx.catalog.defaultLoadout[ctx.selectedUnit] as string);
  }
  const rightW = ctx.catalog.weapons.find((w) => w.id === rightId);
  const leftW  = leftId ? ctx.catalog.weapons.find((w) => w.id === leftId) : null;
  // Left weapon (paladin only)
  if (isPaladin && leftW) {
    const li = document.createElement('img');
    li.src = leftW.sprite;
    li.className = 'cosm-preview-weapon cosm-preview-weapon-l';
    li.alt = leftW.name;
    li.title = 'Clique pour éditer la main gauche';
    li.addEventListener('click', () => {
      ctx.paladinSlot = 'left';
      ctx.selectedCategory = defaultCategoryFor('paladin', 'left');
      redrawAll();
    });
    stack.appendChild(li);
  }
  // Body
  const body = document.createElement('img');
  const bodyKind = ctx.selectedUnit === 'hero' ? 'hero' : 'army';
  body.src = `/sprites/${ctx.previewFactionId}_${bodyKind}.png`;
  body.className = 'cosm-preview-body';
  body.alt = '';
  stack.appendChild(body);
  // Right weapon
  if (rightW) {
    const ri = document.createElement('img');
    ri.src = rightW.sprite;
    ri.className = 'cosm-preview-weapon cosm-preview-weapon-r';
    ri.alt = rightW.name;
    if (isPaladin) ri.title = 'Clique pour éditer la main droite';
    ri.addEventListener('click', () => {
      if (isPaladin) {
        ctx.paladinSlot = 'right';
        ctx.selectedCategory = defaultCategoryFor('paladin', 'right');
        redrawAll();
      }
    });
    stack.appendChild(ri);
  }
  card.appendChild(stack);
  // Paladin slot indicator
  if (isPaladin) {
    const slotInfo = el(
      'div',
      'cosm-preview-slot',
      ctx.paladinSlot === 'right'
        ? 'Modification : main droite — clique la main gauche dans l\'aperçu pour passer.'
        : 'Modification : main gauche — clique la main droite pour passer.',
    );
    card.appendChild(slotInfo);
  }
  return card;
}

/** Tab strip: WEAPON / BADGE … (we only have weapons for now but laying it
 *  out matches the reference screenshot). */
function buildCategoryTabs(ctx: CosmeticsCtx): HTMLElement {
  const tabs = el('div', 'cosm-tabs');
  for (const cat of ctx.catalog.categories) {
    // Hide tabs that have no compatible weapon for the current slot — keeps
    // the strip clean (e.g. an archer doesn't see "Hache").
    const hasAny = ctx.catalog.weapons.some(
      (w) => w.category === cat.id && isCompatible(w, ctx),
    );
    if (!hasAny) continue;
    const t = document.createElement('button');
    t.type = 'button';
    t.className = 'cosm-tab' + (cat.id === ctx.selectedCategory ? ' is-active' : '');
    t.textContent = cat.label.toUpperCase();
    t.addEventListener('click', () => {
      ctx.selectedCategory = cat.id;
      redrawAll();
    });
    tabs.appendChild(t);
  }
  return tabs;
}

/** Grid of weapon cards for the current category. */
function buildGrid(ctx: CosmeticsCtx): HTMLElement {
  const grid = el('div', 'cosm-grid');
  const equippedId = currentWeaponId(ctx);
  const items = ctx.catalog.weapons.filter(
    (w) => w.category === ctx.selectedCategory && isCompatible(w, ctx),
  );
  if (items.length === 0) {
    grid.appendChild(el('p', 'cosm-empty', 'Aucune arme dans cette catégorie pour cette unité.'));
    return grid;
  }
  for (const w of items) {
    const card = document.createElement('div');
    const owned = isOwned(w.id);
    const isEquipped = w.id === equippedId;
    card.className =
      'cosm-card' +
      (isEquipped ? ' is-equipped' : '') +
      (owned ? ' is-owned' : ' is-locked');
    // Sprite
    const img = document.createElement('img');
    img.src = w.sprite;
    img.className = 'cosm-card-img';
    img.alt = w.name;
    card.appendChild(img);
    // Name
    card.appendChild(el('div', 'cosm-card-name', w.name));
    // Unlock chip (price) OR equip status
    if (!owned) {
      const lock = document.createElement('button');
      lock.type = 'button';
      lock.className = 'cosm-card-lock';
      lock.innerHTML = `Débloquer · <span>${w.price}</span> ✨`;
      lock.title = `Coûte ${w.price} Moustaches d'or`;
      lock.addEventListener('click', (ev) => {
        ev.stopPropagation();
        void handleUnlock(w);
      });
      card.appendChild(lock);
    } else if (isEquipped) {
      card.appendChild(el('div', 'cosm-card-equipped', '✔ Équipée'));
    } else {
      card.appendChild(el('div', 'cosm-card-equip', 'Équiper'));
    }
    // Click whole card = equip (only if owned, otherwise prompt unlock)
    card.addEventListener('click', () => {
      if (owned) void handleEquip(w);
      else void handleUnlock(w);
    });
    grid.appendChild(card);
  }
  return grid;
}

/** Unlock flow — deduct shinies via the server, refresh user, re-render. */
async function handleUnlock(w: WeaponDef): Promise<void> {
  if (!auth.user) return;
  if ((auth.user.shinies ?? 0) < w.price) {
    // Soft toast: prepend a temporary banner.
    flashBanner(`Pas assez de Moustaches d'or — il en manque ${w.price - (auth.user.shinies ?? 0)}.`, 'err');
    return;
  }
  try {
    const r = await unlockWeapon(w.id);
    // Patch the cached user in place so the UI updates without a full /me round-trip.
    auth.user = { ...auth.user, shinies: r.shinies, cosmeticUnlocks: r.cosmeticUnlocks };
    flashBanner(`${w.name} débloquée !`, 'ok');
    redrawAll();
  } catch (err) {
    console.error(err);
    flashBanner('Le débloquage a échoué.', 'err');
  }
}

/** Equip flow — saves the loadout server-side, invalidates the in-game cache. */
async function handleEquip(w: WeaponDef): Promise<void> {
  if (!auth.user || !ctxState) return;
  const ctx = ctxState;
  const next = buildLoadoutWith(
    auth.user.cosmeticLoadout ?? {},
    ctx.selectedUnit,
    ctx.selectedUnit === 'paladin' ? ctx.paladinSlot : 'right',
    w.id,
  );
  try {
    const r = await saveLoadout(next);
    auth.user = { ...auth.user, cosmeticLoadout: r.cosmeticLoadout };
    invalidateArmyTextureCache();
    redrawAll();
  } catch (err) {
    console.error(err);
    flashBanner('Impossible d\'équiper cette arme.', 'err');
  }
}

/** Tiny inline toast — shows for ~2.5s above the panel. */
let activeBanner: HTMLElement | null = null;
function flashBanner(msg: string, kind: 'ok' | 'err'): void {
  if (!rootEl) return;
  if (activeBanner) activeBanner.remove();
  const b = el('div', `cosm-banner cosm-banner-${kind}`, msg);
  rootEl.prepend(b);
  activeBanner = b;
  setTimeout(() => {
    if (b.isConnected) b.remove();
    if (activeBanner === b) activeBanner = null;
  }, 2500);
}
